import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import DancerThumbnail from './DancerThumbnail';
import './SponsorshipManager.css';

// Function to calculate string similarity (reused from DancerSearch)
function calculateSimilarity(str1, str2) {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 100;
  if (s1.length === 0 || s2.length === 0) return 0;
  
  // Simple contains check for partial matches
  if (s1.includes(s2) || s2.includes(s1)) {
    const longerLength = Math.max(s1.length, s2.length);
    const shorterLength = Math.min(s1.length, s2.length);
    return Math.round((shorterLength / longerLength) * 100);
  }
  
  return 0;
}

export default function SponsorshipManager() {
  const [allDancers, setAllDancers] = useState([]);
  const [sponsoredDancers, setSponsoredDancers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sponsorshipLoading, setSponsorshipLoading] = useState(false);

  // Load all dancers and current sponsorships
  useEffect(() => {
    loadDancers();
    loadSponsorships();
  }, []);

  async function loadDancers() {
    try {
      const dancersSet = new Set();
      
      // Load from old structure
      const oldSnapshot = await getDocs(collection(db, "competitions"));
      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results) {
          comp.results.forEach((result) => {
            if (result.dancerName) {
              dancersSet.add(result.dancerName);
            }
          });
        }
      });

      // Load from new structure
      const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
      for (const disciplineDoc of disciplinesSnapshot.docs) {
        const disciplineName = disciplineDoc.id;
        const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
        
        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryName = categoryDoc.id;
          
          // Check direct competitions
          try {
            const directCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
            directCompsSnapshot.forEach((doc) => {
              const comp = doc.data();
              if (comp.results) {
                comp.results.forEach((result) => {
                  if (result.dancerName) {
                    dancersSet.add(result.dancerName);
                  }
                });
              }
            });
          } catch (error) {
            // Category might not have direct competitions
          }
          
          // Check dance-specific competitions
          try {
            const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
            for (const danceDoc of dancesSnapshot.docs) {
              const danceName = danceDoc.id;
              const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
              danceCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (comp.results) {
                  comp.results.forEach((result) => {
                    if (result.dancerName) {
                      dancersSet.add(result.dancerName);
                    }
                  });
                }
              });
            }
          } catch (error) {
            // Category might not have dance-specific competitions
          }
        }
      }

      const sortedDancers = Array.from(dancersSet).sort();
      setAllDancers(sortedDancers);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dancers:', error);
      setLoading(false);
    }
  }

  async function loadSponsorships() {
    try {
      const sponsorshipSnapshot = await getDocs(collection(db, "sponsorships"));
      const sponsored = [];
      sponsorshipSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.sponsor === 'prima') {
          sponsored.push(doc.id); // Document ID is the dancer name
        }
      });
      setSponsoredDancers(sponsored);
    } catch (error) {
      console.error('Error loading sponsorships:', error);
    }
  }

  // Search functionality
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const filteredSuggestions = allDancers
        .map(dancer => ({
          name: dancer,
          similarity: calculateSimilarity(dancer, searchTerm)
        }))
        .filter(item => item.similarity >= 30) // Minimum 30% similarity
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 10) // Limit to top 10 matches
        .map(item => ({ name: item.name }));
      
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, allDancers]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const addSponsorship = async (dancerName) => {
    setSponsorshipLoading(true);
    try {
      await setDoc(doc(db, "sponsorships", dancerName), {
        sponsor: 'prima',
        dateAdded: new Date(),
        dancerName: dancerName
      });
      
      setSponsoredDancers(prev => [...prev, dancerName]);
      setSearchTerm('');
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error adding sponsorship:', error);
      alert('Error adding sponsorship. Please try again.');
    }
    setSponsorshipLoading(false);
  };

  const removeSponsorship = async (dancerName) => {
    setSponsorshipLoading(true);
    try {
      await deleteDoc(doc(db, "sponsorships", dancerName));
      setSponsoredDancers(prev => prev.filter(name => name !== dancerName));
    } catch (error) {
      console.error('Error removing sponsorship:', error);
      alert('Error removing sponsorship. Please try again.');
    }
    setSponsorshipLoading(false);
  };

  if (loading) {
    return (
      <div className="sponsorship-page">
        <div className="sponsorship-header">
          <h2>👟 Prima Dance Shoes Sponsorship</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dancers from database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sponsorship-page">
      <div className="sponsorship-header">
        <h2>👟 Prima Dance Shoes Sponsorship</h2>
        <p>Manage dancers sponsored by Prima Dance Shoes</p>
      </div>

      <div className="search-section">
        <h3>Add New Sponsored Dancer</h3>
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search for dancer to sponsor..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              autoComplete="off"
            />
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="clear-button"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {showSuggestions && (
            <div className="suggestions-container">
              {suggestions.length > 0 ? (
                <>
                  <div className="suggestions-header">
                    Found {suggestions.length} dancer{suggestions.length !== 1 ? 's' : ''} matching "{searchTerm}":
                  </div>
                  <div className="suggestions-list">
                    {suggestions.map((dancer) => (
                      <div
                        key={dancer.name}
                        className="suggestion-item"
                      >
                        <div className="dancer-info">
                          <DancerThumbnail 
                            dancerName={dancer.name} 
                            size={48} 
                            showPlaceholder={true}
                          />
                          <div className="dancer-details">
                            <div className="dancer-name">{dancer.name}</div>
                            {sponsoredDancers.includes(dancer.name) ? (
                              <div className="already-sponsored">Already sponsored</div>
                            ) : (
                              <button 
                                onClick={() => addSponsorship(dancer.name)}
                                disabled={sponsorshipLoading}
                                className="add-sponsorship-button"
                              >
                                {sponsorshipLoading ? 'Adding...' : 'Add Sponsorship'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="no-suggestions">
                  No dancers found matching "{searchTerm}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="sponsored-section">
        <h3>Currently Sponsored Dancers ({sponsoredDancers.length})</h3>
        {sponsoredDancers.length === 0 ? (
          <p className="no-sponsored">No dancers are currently sponsored by Prima Dance Shoes.</p>
        ) : (
          <div className="sponsored-list">
            {sponsoredDancers.map((dancerName) => (
              <div key={dancerName} className="sponsored-item">
                <div className="dancer-info">
                  <DancerThumbnail 
                    dancerName={dancerName} 
                    size={48} 
                    showPlaceholder={true}
                  />
                  <div className="dancer-details">
                    <div className="dancer-name">{dancerName}</div>
                    <div className="sponsored-status">Sponsored by Prima Dance Shoes</div>
                  </div>
                </div>
                <button 
                  onClick={() => removeSponsorship(dancerName)}
                  disabled={sponsorshipLoading}
                  className="remove-sponsorship-button"
                >
                  {sponsorshipLoading ? 'Removing...' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
